<template>
    <div class="container mortgage-calculator" :class="calculatorName">

        <div class="row justify-content-center calculator-header">
			<CalculatorHeader>
				<h2>Self Employed Mortgage Calculator</h2>
				<h4>Fill in a few details to see if you qualify for a Self Employed Mortgage</h4>
			</CalculatorHeader>
		</div>
		<div class="row justify-content-center calculator-body">
			<div class="col-md-10 col-xl-8">
				<form class="calculator-container">

					<div class="mt-5" @keyup="resetCalculation" @change="resetCalculation">

						<BooleanChoiceButtons
							fieldText="Are you purchasing or remortgaging?"
							buttonTextLeft="Purchasing"
							buttonTextRight="Remortgaging"								
						>
							<template v-slot:left>
								<input type="radio" v-model="startOption" value="Purchasing" @click="clearRemortgagingValues">									
							</template>
							<template v-slot:right>
								<input type="radio" v-model="startOption" value="Remortgaging" @click="clearPurchasingValues">										
							</template>																
						</BooleanChoiceButtons>

						<transition name="scale-in" mode="out-in">
							<div key="Purchasing" v-if="startOption === 'Purchasing'">
								<BooleanChoiceButtons				
									fieldText="Do you have a property in mind?"
									buttonTextLeft="Yes"
									buttonTextRight="No"								
								>
									<template v-slot:left>
										<input type="radio" v-model="propertyOption" value="Yes" @click="clearPurchaseValue">									
									</template>
									<template v-slot:right>
										<input type="radio" v-model="propertyOption" value="No" @click="clearPurchasePrice">									
									</template>																
								</BooleanChoiceButtons>

								<transition name="fade-x" mode="out-in">
									<div v-if="propertyOption !==''">
										<transition name="scale-in" mode="out-in">
											<div key="Yes" v-if="propertyOption === 'Yes'" >
												<InputElement fieldText="What is the purchase price?" >
													<InputTypeNumber currencyPlaceholder v-model="purchasePrice" />
												</InputElement>
											</div>
											<div key="No" v-if="propertyOption === 'No'" >
												<InputElement fieldText="Approximately, what value would you like to purchase at?">
													<InputTypeNumber currencyPlaceholder v-model="purchaseValue" />
												</InputElement>
											</div>								
										</transition>
										<transition name="fade-x" mode="out-in">
											<InputElement
												v-if="purchasePrice > 0 || purchaseValue > 0"
												fieldText="What is your required mortgage amount?"
											>
												<InputTypeNumber currencyPlaceholder v-model="mortgageAmount" />
											</InputElement>
										</transition>																	
									</div>						
								</transition>
							</div>

							<div key="Remortgaging" v-if="startOption === 'Remortgaging'">
								<InputElement
									fieldText="What is the approximate value of your property?"
								>
									<InputTypeNumber currencyPlaceholder v-model="approximateValue" />
								</InputElement>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="approximateValue > 0"
										fieldText="What is your current outstanding mortgage amount?"
									>
										<InputTypeNumber currencyPlaceholder v-model="outstandingMortgage" />
									</InputElement>
								</transition>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="approximateValue > 0 && outstandingMortgage"
										fieldText="Who is your current lender?"
									>
										<input class="form-control calc-form-control" type="text" v-model="currentLender">
									</InputElement>
								</transition>

								<transition name="fade-x" mode="out-in">
									<BooleanChoiceButtons
										v-show="approximateValue > 0 && outstandingMortgage && currentLender"
										fieldText="Are you remortgaging with additional borrowing?"
										buttonTextLeft="Yes"
										buttonTextRight="No"							
									>
										<template v-slot:left>
											<input type="radio" v-model="additionalBorrowing" value="Yes">								
										</template>
										<template v-slot:right>
											<input type="radio" v-model="additionalBorrowing" value="No">									
										</template>																
									</BooleanChoiceButtons>
								</transition>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="approximateValue > 0 && outstandingMortgage && currentLender && additionalBorrowing === 'Yes'"
										fieldText="How much more would you like to borrow?"
									>
										<InputTypeNumber currencyPlaceholder v-model="additionalBorrowAmount" />
									</InputElement>																		
								</transition>
							</div>
						</transition>					
					
						<transition name="fade-x" mode="out-in">
							<div v-if="sectionCompleted">
								<BooleanChoiceButtons
									fieldText="Number of applicants*"
									buttonTextLeft="One applicant"
									buttonTextRight="Two applicants"						
								>
									<template v-slot:left>
										<input type="radio" v-model="applicantsCount" value=1 @click="clearApplicant2">								
									</template>
									<template v-slot:right>
										<input type="radio" v-model="applicantsCount" value=2 >									
									</template>																
								</BooleanChoiceButtons>

								<transition name="fade-x" mode="out-in">
									<div class="mb-5" v-if="applicantsCount > 0">
										<h4 class="calc-fieldset-title">First Applicant</h4>
										<div class="row mb-5">
											<div class="col-md-6 col-lg-4">Employment status</div>
											<div class="col-md-6 col-lg-8" @change="clearApplicantEmployment1">
												<DropdownMenu :dropdownOptions="dropdownOptions" v-model="applicantEmployment1"/>
											</div>
										</div>
									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--sole-trader section completed-->
									<div v-if="applicantEmployment1 === 'sole-trader'">
										<div class="row mb-5">
											<div class="col-md-6 col-lg-4">How long have you been trading?</div>
											<div class="col-md-6 col-lg-8 fcs-grid-1-2">
												<MultiplyChoiceButton
													buttonText="0 years"
												>
													<input type="radio" v-model="soleTraderYearsTrading1" value="0">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="1 year"
												>
													<input type="radio" v-model="soleTraderYearsTrading1" value="1">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="2 years"
												>
													<input type="radio" v-model="soleTraderYearsTrading1" value="2">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="3+ years"
												>
													<input type="radio" v-model="soleTraderYearsTrading1" value="3plus">
												</MultiplyChoiceButton>
											</div>
										</div>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'sole-trader' && soleTraderYearsTrading1"
												fieldText="Do you have an accountant?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="soleTraderAccoutantOption1" value="Yes">							
												</template>
												<template v-slot:right>
													<input type="radio" v-model="soleTraderAccoutantOption1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'sole-trader' && soleTraderYearsTrading1 && soleTraderAccoutantOption1"
												fieldText="Are your tax returns up-to-date?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="soleTraderTaxReturns1" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="soleTraderTaxReturns1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'sole-trader' && soleTraderYearsTrading1 && soleTraderAccoutantOption1 && soleTraderTaxReturns1"
												fieldText="Has there been a substantial change in net profit in the last 2 years?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="soleTraderprofitChange1" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="soleTraderprofitChange1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment1 === 'sole-trader' && soleTraderYearsTrading1 && soleTraderAccoutantOption1 && soleTraderTaxReturns1 && soleTraderprofitChange1"
												fieldText="Your latest years net profit"
											>
												<InputTypeNumber currencyPlaceholder v-model="soleTraderLatestProfit1" />
											</InputElement>
										</transition>

									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--partnership section completed-->
									<div v-if="applicantEmployment1 === 'partnership'">
										<div class="row mb-5">
											<div class="col-md-6 col-lg-4">How long have you been trading?</div>
											<div class="col-md-6 col-lg-8 fcs-grid-1-2">
												<MultiplyChoiceButton
													buttonText="0 years"
												>
													<input type="radio" v-model="partnershipYearsTrading1" value="0">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="1 year"
												>
													<input type="radio" v-model="partnershipYearsTrading1" value="1">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="2 years"
												>
													<input type="radio" v-model="partnershipYearsTrading1" value="2">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="3+ years"
												>
													<input type="radio" v-model="partnershipYearsTrading1" value="3plus">
												</MultiplyChoiceButton>
											</div>
										</div>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'partnership' && partnershipYearsTrading1"
												fieldText="Do you have an accountant?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="partnershipAccoutantOption1" value="Yes">							
												</template>
												<template v-slot:right>
													<input type="radio" v-model="partnershipAccoutantOption1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'partnership' && partnershipYearsTrading1 && partnershipAccoutantOption1"
												fieldText="Are your tax returns up-to-date?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="partnershipTaxReturns1" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="partnershipTaxReturns1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'partnership' && partnershipYearsTrading1 && partnershipAccoutantOption1 && partnershipTaxReturns1"
												fieldText="Has there been a substantial change in net profit in the last 2 years?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="partnershipProfitChange1" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="partnershipProfitChange1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment1 === 'partnership' && partnershipYearsTrading1 && partnershipAccoutantOption1 && partnershipTaxReturns1 && partnershipProfitChange1"
												fieldText="What percentage shareholding do you have?"
											>
												<InputTypeNumber v-model="partnershipShareholdPercentage1" />
											</InputElement>
										</transition>																													

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment1 === 'partnership' && partnershipYearsTrading1 && partnershipAccoutantOption1 && partnershipTaxReturns1 && partnershipProfitChange1 && partnershipShareholdPercentage1"
												fieldText="What is your share of the latest years net profit?"
											>
												<InputTypeNumber currencyPlaceholder v-model="partnershipShareholdProfit1" />
											</InputElement>
										</transition>

									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--director section completed-->
									<div v-if="applicantEmployment1 === 'director-with-shareholding'">
										<div class="row mb-5">
											<div class="col-md-6 col-lg-4">How long have you been a shareholding Director?</div>
											<div class="col-md-6 col-lg-8 fcs-grid-1-2">
												<MultiplyChoiceButton
													buttonText="0 years"
												>
													<input type="radio" v-model="directorYearsTrading1" value="0">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="1 year"
												>
													<input type="radio" v-model="directorYearsTrading1" value="1">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="2 years"
												>
													<input type="radio" v-model="directorYearsTrading1" value="2">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="3+ years"
												>
													<input type="radio" v-model="directorYearsTrading1" value="3plus">
												</MultiplyChoiceButton>
											</div>
										</div>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'director-with-shareholding' && directorYearsTrading1"
												fieldText="Do you have an accountant?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="directorAccoutantOption1" value="Yes">							
												</template>
												<template v-slot:right>
													<input type="radio" v-model="directorAccoutantOption1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'director-with-shareholding' && directorYearsTrading1 && directorAccoutantOption1"
												fieldText="Are your tax returns up-to-date?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="directorTaxReturns1" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="directorTaxReturns1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'director-with-shareholding' && directorYearsTrading1 && directorAccoutantOption1 && directorTaxReturns1"
												fieldText="Have you recently changed trading style?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="directorTradingStyle1" value="Yes">				
												</template>
												<template v-slot:right>
													<input type="radio" v-model="directorTradingStyle1" value="No">					
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment1 === 'director-with-shareholding' && directorYearsTrading1 && directorAccoutantOption1 && directorTaxReturns1 && directorTradingStyle1"
												fieldText="Your salary drawn"
											>
												<InputTypeNumber currencyPlaceholder v-model="directorSalaryDrawn1" />
											</InputElement>	
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment1 === 'director-with-shareholding' && directorYearsTrading1 && directorAccoutantOption1 && directorTaxReturns1 && directorTradingStyle1 && directorSalaryDrawn1"
												fieldText="Dividends for the last 12 months"
											>
												<InputTypeNumber currencyPlaceholder v-model="directorYearDividends1" />
											</InputElement>
										</transition>																		

									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--contractor section -->
									<div v-if="applicantEmployment1 === 'contractor'">
										<BooleanChoiceButtons
											fieldText="Have you been contracting for at least 6 months?"
											buttonTextLeft="Yes"
											buttonTextRight="No"								
										>
											<template v-slot:left>
												<input type="radio" v-model="contractorMonths1" value="Yes">								
											</template>
											<template v-slot:right>
												<input type="radio" v-model="contractorMonths1" value="No">									
											</template>																
										</BooleanChoiceButtons>

										<transition name="fade-x" mode="out-in">
											<div class="row mb-5" v-if="applicantEmployment1 === 'contractor' && contractorMonths1 === 'Yes'">
												<div class="col-md-6 col-lg-4">If so, how long for?</div>
												<div class="col-md-6 col-lg-8 fcs-grid-1-2">
													<MultiplyChoiceButton
														buttonText="0 years"
													>
														<input type="radio" v-model="contractorYearsTrading1" value="0">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="1 year"
													>
														<input type="radio" v-model="contractorYearsTrading1" value="1">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="2 years"
													>
														<input type="radio" v-model="contractorYearsTrading1" value="2">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="3+ years"
													>
														<input type="radio" v-model="contractorYearsTrading1" value="3plus">
													</MultiplyChoiceButton>
												</div>
											</div>
										</transition>										

										<transition name="fade-x" mode="out-in">									
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'contractor' && contractorMonths1"
												fieldText="Do you have an accountant?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="contractorAccountantOption1" value="Yes">							
												</template>
												<template v-slot:right>
													<input type="radio" v-model="contractorAccountantOption1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'contractor' && contractorMonths1 && contractorAccountantOption1"
												fieldText="Are your tax returns up-to-date?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="contractorTaxReturns1" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="contractorTaxReturns1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment1 === 'contractor' && contractorMonths1 && contractorAccountantOption1 && contractorTaxReturns1"
												fieldText="What is your daily rate?"
											>
												<InputTypeNumber currencyPlaceholder v-model="contractorDailyRate1" />
											</InputElement>
										</transition>								

									</div>
								</transition>	

								<transition name="fade-x" mode="out-in"> <!--CIS section completed-->
									<div v-if="applicantEmployment1 === 'cis-worker'">
										<BooleanChoiceButtons
											fieldText="Have you been sub-contracting for at least 12 months?"
											buttonTextLeft="Yes"
											buttonTextRight="No"								
										>
											<template v-slot:left>
												<input type="radio" v-model="subcontractorMonths1" value="Yes">								
											</template>
											<template v-slot:right>
												<input type="radio" v-model="subcontractorMonths1" value="No">								
											</template>																
										</BooleanChoiceButtons>

										<transition name="fade-x" mode="out-in">
											<div class="row mb-5" v-if="applicantEmployment1 === 'cis-worker' && subcontractorMonths1 === 'Yes'">
												<div class="col-md-6 col-lg-4">If so, how long for?</div>
												<div class="col-md-6 col-lg-8 fcs-grid-1-2">
													<MultiplyChoiceButton
														buttonText="0 years"
													>
														<input type="radio" v-model="subcontractorYearsTrading1" value="0">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="1 year"
													>
														<input type="radio" v-model="subcontractorYearsTrading1" value="1">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="2 years"
													>
														<input type="radio" v-model="subcontractorYearsTrading1" value="2">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="3+ years"
													>
														<input type="radio" v-model="subcontractorYearsTrading1" value="3plus">
													</MultiplyChoiceButton>
												</div>
											</div>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'cis-worker' && subcontractorMonths1"
												fieldText="Have you worked for the same contractor for 12 months?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="subcontractorSameContractor1" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="subcontractorSameContractor1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment1 === 'cis-worker' && subcontractorMonths1 && subcontractorSameContractor1"
												fieldText="Does the company you work for arrange the payment of your tax?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="subcontractorPaymentOfTax1" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="subcontractorPaymentOfTax1" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment1 === 'cis-worker' && subcontractorMonths1 && subcontractorSameContractor1 && subcontractorPaymentOfTax1"
												fieldText="What is your annual income?"
											>
												<InputTypeNumber currencyPlaceholder v-model="subcontractorAnnualIcome1" />
											</InputElement>
										</transition>
									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--Employed section completed -->
									<InputElement v-if="applicantEmployment1 === 'employed'"
										fieldText="What is your annual income?"
									>
										<InputTypeNumber currencyPlaceholder v-model="employedAnnualIncome1" />
									</InputElement>
								</transition>

								<transition name="fade-x" mode="out-in">
									<div class="row mb-5" v-if="applicantsCount == 2">
										<h4 class="calc-fieldset-title">Second Applicant</h4>
										<div class="col-md-6 col-lg-4">Employment status</div>
										<div class="col-md-6 col-lg-8" @change="clearApplicantEmployment2">
											<DropdownMenu :dropdownOptions="dropdownOptions" v-model="applicantEmployment2" />
										</div>
									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--sole-trader section completed-->
									<div v-if="applicantEmployment2 === 'sole-trader'">
										<div class="row mb-5">
											<div class="col-md-6 col-lg-4">How long have you been trading?</div>
											<div class="col-md-6 col-lg-8 fcs-grid-1-2">
												<MultiplyChoiceButton
													buttonText="0 years"
												>
													<input type="radio" v-model="soleTraderYearsTrading2" value="0">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="1 year"
												>
													<input type="radio" v-model="soleTraderYearsTrading2" value="1">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="2 years"
												>
													<input type="radio" v-model="soleTraderYearsTrading2" value="2">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="3+ years"
												>
													<input type="radio" v-model="soleTraderYearsTrading2" value="3plus">
												</MultiplyChoiceButton>
											</div>
										</div>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'sole-trader' && soleTraderYearsTrading2"
												fieldText="Do you have an accountant?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="soleTraderAccoutantOption2" value="Yes">							
												</template>
												<template v-slot:right>
													<input type="radio" v-model="soleTraderAccoutantOption2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'sole-trader' && soleTraderYearsTrading2 && soleTraderAccoutantOption2"
												fieldText="Are your tax returns up-to-date?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="soleTraderTaxReturns2" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="soleTraderTaxReturns2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'sole-trader' && soleTraderYearsTrading2 && soleTraderAccoutantOption2 && soleTraderTaxReturns2"
												fieldText="Has there been a substantial change in net profit in the last 2 years?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="soleTraderprofitChange2" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="soleTraderprofitChange2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment2 === 'sole-trader' && soleTraderYearsTrading2 && soleTraderAccoutantOption2 && soleTraderTaxReturns2 && soleTraderprofitChange2"
												fieldText="Your latest years net profit"
											>
												<InputTypeNumber currencyPlaceholder v-model="soleTraderLatestProfit2" />
											</InputElement>
										</transition>

									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--partnership section completed-->
									<div v-if="applicantEmployment2 === 'partnership'">
										<div class="row mb-5">
											<div class="col-md-6 col-lg-4">How long have you been trading?</div>
											<div class="col-md-6 col-lg-8 fcs-grid-1-2">
												<MultiplyChoiceButton
													buttonText="0 years"
												>
													<input type="radio" v-model="partnershipYearsTrading2" value="0">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="1 year"
												>
													<input type="radio" v-model="partnershipYearsTrading2" value="1">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="2 years"
												>
													<input type="radio" v-model="partnershipYearsTrading2" value="2">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="3+ years"
												>
													<input type="radio" v-model="partnershipYearsTrading2" value="3plus">
												</MultiplyChoiceButton>
											</div>
										</div>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'partnership' && partnershipYearsTrading2"
												fieldText="Do you have an accountant?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="partnershipAccoutantOption2" value="Yes">							
												</template>
												<template v-slot:right>
													<input type="radio" v-model="partnershipAccoutantOption2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'partnership' && partnershipYearsTrading2 && partnershipAccoutantOption2"
												fieldText="Are your tax returns up-to-date?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="partnershipTaxReturns2" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="partnershipTaxReturns2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'partnership' && partnershipYearsTrading2 && partnershipAccoutantOption2 && partnershipTaxReturns2"
												fieldText="Has there been a substantial change in net profit in the last 2 years?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="partnershipProfitChange2" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="partnershipProfitChange2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment2 === 'partnership' && partnershipYearsTrading2 && partnershipAccoutantOption2 && partnershipTaxReturns2 && partnershipProfitChange2"
												fieldText="What percentage shareholding do you have?"
											>
												<InputTypeNumber v-model="partnershipShareholdPercentage2" />
											</InputElement>
										</transition>																													

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment2 === 'partnership' && partnershipYearsTrading2 && partnershipAccoutantOption2 && partnershipTaxReturns2 && partnershipProfitChange2 && partnershipShareholdPercentage2"
												fieldText="What is your share of the latest years net profit?"
											>
												<InputTypeNumber currencyPlaceholder v-model="partnershipShareholdProfit2" />
											</InputElement>
										</transition>

									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--director section completed-->
									<div v-if="applicantEmployment2 === 'director-with-shareholding'">
										<div class="row mb-5">
											<div class="col-md-6 col-lg-4">How long have you been a shareholding Director?</div>
											<div class="col-md-6 col-lg-8 fcs-grid-1-2">
												<MultiplyChoiceButton
													buttonText="0 years"
												>
													<input type="radio" v-model="directorYearsTrading2" value="0">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="1 year"
												>
													<input type="radio" v-model="directorYearsTrading2" value="1">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="2 years"
												>
													<input type="radio" v-model="directorYearsTrading2" value="2">
												</MultiplyChoiceButton>
												<MultiplyChoiceButton
													buttonText="3+ years"
												>
													<input type="radio" v-model="directorYearsTrading2" value="3plus">
												</MultiplyChoiceButton>
											</div>
										</div>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'director-with-shareholding' && directorYearsTrading2"
												fieldText="Do you have an accountant?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="directorAccoutantOption2" value="Yes">							
												</template>
												<template v-slot:right>
													<input type="radio" v-model="directorAccoutantOption2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'director-with-shareholding' && directorYearsTrading2 && directorAccoutantOption2"
												fieldText="Are your tax returns up-to-date?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="directorTaxReturns2" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="directorTaxReturns2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'director-with-shareholding' && directorYearsTrading2 && directorAccoutantOption2 && directorTaxReturns2"
												fieldText="Have you recently changed trading style?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="directorTradingStyle2" value="Yes">				
												</template>
												<template v-slot:right>
													<input type="radio" v-model="directorTradingStyle2" value="No">					
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment2 === 'director-with-shareholding' && directorYearsTrading2 && directorAccoutantOption2 && directorTaxReturns2 && directorTradingStyle2"
												fieldText="Your salary drawn"
											>
												<InputTypeNumber currencyPlaceholder v-model="directorSalaryDrawn2" />
											</InputElement>	
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment2 === 'director-with-shareholding' && directorYearsTrading2 && directorAccoutantOption2 && directorTaxReturns2 && directorTradingStyle2 && directorSalaryDrawn2"
												fieldText="Dividends for the last 12 months"
											>
												<InputTypeNumber currencyPlaceholder v-model="directorYearDividends2" />
											</InputElement>
										</transition>																		

									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--contractor section -->
									<div v-if="applicantEmployment2 === 'contractor'">
										<BooleanChoiceButtons
											fieldText="Have you been contracting for at least 6 months?"
											buttonTextLeft="Yes"
											buttonTextRight="No"								
										>
											<template v-slot:left>
												<input type="radio" v-model="contractorMonths2" value="Yes">								
											</template>
											<template v-slot:right>
												<input type="radio" v-model="contractorMonths2" value="No">									
											</template>																
										</BooleanChoiceButtons>

										<transition name="fade-x" mode="out-in">
											<div class="row mb-5" v-if="applicantEmployment2 === 'contractor' && contractorMonths2 === 'Yes'">
												<div class="col-md-6 col-lg-4">If so, how long for?</div>
												<div class="col-md-6 col-lg-8 fcs-grid-1-2">
													<MultiplyChoiceButton
														buttonText="0 years"
													>
														<input type="radio" v-model="contractorYearsTrading2" value="0">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="1 year"
													>
														<input type="radio" v-model="contractorYearsTrading2" value="1">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="2 years"
													>
														<input type="radio" v-model="contractorYearsTrading2" value="2">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="3+ years"
													>
														<input type="radio" v-model="contractorYearsTrading2" value="3plus">
													</MultiplyChoiceButton>
												</div>
											</div>
										</transition>										

										<transition name="fade-x" mode="out-in">									
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'contractor' && contractorMonths2"
												fieldText="Do you have an accountant?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="contractorAccountantOption2" value="Yes">							
												</template>
												<template v-slot:right>
													<input type="radio" v-model="contractorAccountantOption2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'contractor' && contractorMonths2 && contractorAccountantOption2"
												fieldText="Are your tax returns up-to-date?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="contractorTaxReturns2" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="contractorTaxReturns2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment2 === 'contractor' && contractorMonths2 && contractorAccountantOption2 && contractorTaxReturns2"
												fieldText="What is your daily rate?"
											>
												<InputTypeNumber currencyPlaceholder v-model="contractorDailyRate2" />
											</InputElement>
										</transition>								

									</div>
								</transition>	

								<transition name="fade-x" mode="out-in"> <!--CIS section completed-->
									<div v-if="applicantEmployment2 === 'cis-worker'">
										<BooleanChoiceButtons
											fieldText="Have you been sub-contracting for at least 12 months?"
											buttonTextLeft="Yes"
											buttonTextRight="No"								
										>
											<template v-slot:left>
												<input type="radio" v-model="subcontractorMonths2" value="Yes">								
											</template>
											<template v-slot:right>
												<input type="radio" v-model="subcontractorMonths2" value="No">								
											</template>																
										</BooleanChoiceButtons>

										<transition name="fade-x" mode="out-in">
											<div class="row mb-5" v-if="applicantEmployment2 === 'cis-worker' && subcontractorMonths2 === 'Yes'">
												<div class="col-md-6 col-lg-4">If so, how long for?</div>
												<div class="col-md-6 col-lg-8 fcs-grid-1-2">
													<MultiplyChoiceButton
														buttonText="0 years"
													>
														<input type="radio" v-model="subcontractorYearsTrading2" value="0">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="1 year"
													>
														<input type="radio" v-model="subcontractorYearsTrading2" value="1">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="2 years"
													>
														<input type="radio" v-model="subcontractorYearsTrading2" value="2">
													</MultiplyChoiceButton>
													<MultiplyChoiceButton
														buttonText="3+ years"
													>
														<input type="radio" v-model="subcontractorYearsTrading2" value="3plus">
													</MultiplyChoiceButton>
												</div>
											</div>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'cis-worker' && subcontractorMonths2"
												fieldText="Have you worked for the same contractor for 12 months?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="subcontractorSameContractor2" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="subcontractorSameContractor2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons v-if="applicantEmployment2 === 'cis-worker' && subcontractorMonths2 && subcontractorSameContractor2"
												fieldText="Does the company you work for arrange the payment of your tax?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="subcontractorPaymentOfTax2" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="subcontractorPaymentOfTax2" value="No">							
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement v-if="applicantEmployment2 === 'cis-worker' && subcontractorMonths2 && subcontractorSameContractor2 && subcontractorPaymentOfTax2"
												fieldText="What is your annual income?"
											>
												<InputTypeNumber currencyPlaceholder v-model="subcontractorAnnualIcome2" />
											</InputElement>
										</transition>
									</div>
								</transition>

								<transition name="fade-x" mode="out-in"> <!--Employed section completed -->
									<InputElement v-if="applicantEmployment2 === 'employed'"
										fieldText="What is your annual income?"
									>
										<InputTypeNumber currencyPlaceholder v-model="employedAnnualIncome2" />
									</InputElement>
								</transition>
								
								<!-- <transition name="fade-x" mode="out-in">
									<ReturnedValueBox  :innerElement=false text="Your household income: " :value="householdIncome" />
								</transition>							 -->

							</div>
						</transition>

						<transition name="scale-in" mode="out-in">
							<div
								v-if="showCalculateButton"
								class="text-center mt-5 mb-3"
							>
								<div class="btn calc-btn-secondary" @click="calculate">CALCULATE</div>
							</div>							
						</transition>

					</div>

					<transition name="fade-y" mode="out-in">
						<div v-if="calcCompleted">

							<ResultsContainer class="mt-5 mb-5 calc-results-box">
								<div class="mb-3 results-title">You may be able to borrow up to:</div>
								<span class="fcs-price">£{{ formatNumberOutput(this.amountAvailable) }}</span>
								<div class="mt-4">The figure is an indication of the amount available, subject to full assessment and underwriting. Please refer to one of our specialist advisers.</div>
								<div class="text-uppercase">Complete the contact form below to find the best self employed mortgage rates</div>
							</ResultsContainer>

							<SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody" />

						</div>
					</transition>
				</form>
			</div>
		</div>
		
    </div>
</template>

<script>
//TODO add ReturnedValueBox household income as per FirstTimeBuyer.vue line 43
import BooleanChoiceButtons from '@/components/utilities/booleanChoiceButtons.vue'
import CalculatorHeader from '@/components/utilities/calculatorHeader.vue'
import DropdownMenu from '@/components/utilities/dropdownMenu.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import MultiplyChoiceButton from '@/components/utilities/multiplyChoiceButton.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
// import ReturnedValueBox from '@/components/utilities/returnedValueBox.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'

export default {
	components: {
		BooleanChoiceButtons,
		CalculatorHeader,	
		DropdownMenu,
		InputElement,
		InputTypeNumber,
		MultiplyChoiceButton,
		ResultsContainer,
		// ReturnedValueBox,
		SubmissionForm
	},	
    data() {
        return {
			calculatorName: 'self-employed-calculator',

			startOption: "",
			propertyOption: "",

			//Purchase
			purchasePrice: 0,
			purchaseValue: 0,
			mortgageAmount: 0,

			//Remortgage
			approximateValue: 0,
			outstandingMortgage: null,
			currentLender: '',
			additionalBorrowing: 0,
			additionalBorrowAmount: 0,

			//Applicants section
            applicantsCount: 0,

			applicantEmployment1: '',

			soleTraderYearsTrading1: '',
			soleTraderAccoutantOption1: '',
			soleTraderTaxReturns1: '',
			soleTraderprofitChange1: '',
			soleTraderLatestProfit1: null,

			partnershipYearsTrading1: '',
			partnershipAccoutantOption1: '',
			partnershipTaxReturns1: '',
			partnershipProfitChange1: '',
			partnershipShareholdPercentage1: 0,
			partnershipShareholdProfit1: null,

			directorYearsTrading1: '',
			directorAccoutantOption1: '',
			directorTaxReturns1: '',
			directorTradingStyle1: '',
			directorSalaryDrawn1: 0,
			directorYearDividends1: null,

			contractorMonths1: '',
			contractorYearsTrading1: '',
			contractorAccountantOption1: '',
			contractorTaxReturns1: '',
			contractorDailyRate1: null,

			subcontractorMonths1: '',
			subcontractorYearsTrading1: '',
			subcontractorSameContractor1: '',
			subcontractorPaymentOfTax1: '',
			subcontractorAnnualIcome1: null,

			employedAnnualIncome1: 0,

			applicantEmployment2: '',

			soleTraderYearsTrading2: '',
			soleTraderAccoutantOption2: '',
			soleTraderTaxReturns2: '',
			soleTraderprofitChange2: '',
			soleTraderLatestProfit2: null,

			partnershipYearsTrading2: '',
			partnershipAccoutantOption2: '',
			partnershipTaxReturns2: '',
			partnershipProfitChange2: '',
			partnershipShareholdPercentage2: 0,
			partnershipShareholdProfit2: null,

			directorYearsTrading2: '',
			directorAccoutantOption2: '',
			directorTaxReturns2: '',
			directorTradingStyle2: '',
			directorSalaryDrawn2: 0,
			directorYearDividends2: null,

			contractorMonths2: '',
			contractorYearsTrading2: '',
			contractorAccountantOption2: '',
			contractorTaxReturns2: '',
			contractorDailyRate2: null,

			subcontractorMonths2: '',
			subcontractorYearsTrading2: '',
			subcontractorSameContractor2: '',
			subcontractorPaymentOfTax2: '',
			subcontractorAnnualIcome2: null,

			employedAnnualIncome2: 0,			

			amountAvailable: 0,
			calcCompleted: false,
			dropdownOptions: [
				{ value:"", text: "Choose option" },
				{ value:"sole-trader", text: "Sole Trader" },
				{ value:"partnership", text: "Partnership" },
				{ value:"director-with-shareholding", text: "Director with shareholding" },
				{ value:"contractor", text: "Contractor" },
				{ value:"cis-worker", text: "CIS worker" },
				{ value:"employed", text: "Employed" }
			]
        }
    },
    methods: {		
		calculate() {
			this.amountAvailable = parseInt(this.calculateQuote)
			this.calcCompleted = true
        },
		formatNumberOutput(number) {
			const newNumber = (new Intl.NumberFormat('en-GB').format(number))
			if ( newNumber === 'NaN' ) { return 0 } return newNumber
		},
		removeHyphensToUpperCase(str) {
            return str.replace(/(-|^)([^-]?)/g, function(_, prep, letter) { return (prep && ' ') + letter.toUpperCase(); });
        },
        clearPurchasePrice() {
			this.purchasePrice = 0,
			this.mortgageAmount = 0
		},
		clearPurchaseValue() {
			this.purchaseValue = 0,
			this.mortgageAmount = 0
		},		
		clearPurchasingValues() {
			this.purchasePrice = 0,
			this.purchaseValue = 0,
			this.mortgageAmount = 0			
		},		
		clearRemortgagingValues() {
			this.approximateValue =  0,
			this.outstandingMortgage = null,
			this.currentLender = '',
			this.additionalBorrowing =  0,
			this.additionalBorrowAmount = 0			
		},
		clearApplicantEmployment1() {
			this.soleTraderYearsTrading1 = '',
			this.soleTraderAccoutantOption1 = '',
			this.soleTraderTaxReturns1 = '',
			this.soleTraderprofitChange1 = '',
			this.soleTraderLatestProfit1 = null,

			this.partnershipYearsTrading1 = '',
			this.partnershipAccoutantOption = '',
			this.partnershipTaxReturns1 = '',
			this.partnershipProfitChange1 = '',
			this.partnershipShareholdPercentage1 = 0,
			this.partnershipShareholdProfit1 = null,

			this.directorYearsTrading1 = '',
			this.directorAccoutantOption1 = '',
			this.directorTaxReturns1 = '',
			this.directorTradingStyle1 = '',
			this.directorSalaryDrawn1 = 0,
			this.directorYearDividends1 = null,

			this.contractorMonths1 = '',
			this.contractorYearsTrading1 = '',
			this.contractorAccountantOption1 = '',
			this.contractorTaxReturns1 = '',
			this.contractorDailyRate1 = null,

			this.subcontractorMonths1 = '',
			this.subcontractorYearsTrading1 = '',
			this.subcontractorSameContractor1 = '',
			this.subcontractorPaymentOfTax1 = '',
			this.subcontractorAnnualIcome1 = null,

			this.employedAnnualIncome1 = 0
		},
		clearApplicantEmployment2() {
			this.soleTraderYearsTrading2 = '',
			this.soleTraderAccoutantOption2 = '',
			this.soleTraderTaxReturns2 = '',
			this.soleTraderprofitChange2 = '',
			this.soleTraderLatestProfit2 = null,

			this.partnershipYearsTrading2 = '',
			this.partnershipAccoutantOption = '',
			this.partnershipTaxReturns2 = '',
			this.partnershipProfitChange2 = '',
			this.partnershipShareholdPercentage2 = 0,
			this.partnershipShareholdProfit2 = null,

			this.directorYearsTrading2 = '',
			this.directorAccoutantOption2 = '',
			this.directorTaxReturns2 = '',
			this.directorTradingStyle2 = '',
			this.directorSalaryDrawn2 = 0,
			this.directorYearDividends2 = null,

			this.contractorMonths2 = '',
			this.contractorYearsTrading2 = '',
			this.contractorAccountantOption2 = '',
			this.contractorTaxReturns2 = '',
			this.contractorDailyRate2 = null,

			this.subcontractorMonths2 = '',
			this.subcontractorYearsTrading2 = '',
			this.subcontractorSameContractor2 = '',
			this.subcontractorPaymentOfTax2 = '',
			this.subcontractorAnnualIcome2 = null,

			this.employedAnnualIncome2 = 0
		},
		clearApplicant2() {
			this.applicantEmployment2 = ''
			this.clearApplicantEmployment2()
		},		
		resetCalculation() {
			this.calcCompleted = false;
		},
		valueToZero(value) {
			if ( !Number.isInteger(value) ) { return 0 } return value
		},
		formatEmptyMessageFields(message){
			return message ? message : 'N/A'
		}							
    },	
    computed: {
		/**
		 * Check if previous logical section is completed
		 * @return {Bool}
		 */
        sectionCompleted() {
			return ( this.mortgageAmount > 0 || this.additionalBorrowing === 'No' || this.additionalBorrowAmount > 0 ) ? true : false
		},
		/**
		 * Show button if previous section is completed
		 * @return {Bool}
		 */		
		showCalculateButton() {
			let calculateButton
			if ( this.applicantsCount < 1 ) { return calculateButton = false }
			this.applicantsCount == 1
				? ( this.soleTraderLatestProfit1 || this.partnershipShareholdProfit1 || this.directorYearDividends1 || this.contractorDailyRate1 || this.subcontractorAnnualIcome1 || this.employedAnnualIncome1 ) ? calculateButton = true : calculateButton = false
				: ( this.soleTraderLatestProfit2 || this.partnershipShareholdProfit2 || this.directorYearDividends2 || this.contractorDailyRate2 || this.subcontractorAnnualIcome2 || this.employedAnnualIncome2 ) && ( this.soleTraderLatestProfit1 || this.partnershipShareholdProfit1 || this.directorYearDividends1 || this.contractorDailyRate1 || this.subcontractorAnnualIcome1 || this.employedAnnualIncome1 ) ? calculateButton = true : calculateButton = false  
			return calculateButton			
		},
		/**
		 * Calculate the quote of the available credit
		 * @return {Number}
		 */					
		calculateQuote() {
			return (this.valueToZero(this.soleTraderLatestProfit1) + this.valueToZero(this.partnershipShareholdProfit1) + this.valueToZero(this.directorYearDividends1) + this.valueToZero(this.directorSalaryDrawn1) + this.valueToZero(this.subcontractorAnnualIcome1) + this.valueToZero(this.employedAnnualIncome1) + this.valueToZero(this.soleTraderLatestProfit2) + this.valueToZero(this.partnershipShareholdProfit2) + this.valueToZero(this.directorYearDividends2) + this.valueToZero(this.directorSalaryDrawn2) + this.valueToZero(this.subcontractorAnnualIcome2) + this.valueToZero(this.employedAnnualIncome2) + ((this.valueToZero(this.contractorDailyRate1) * 5) * 48) + ((this.valueToZero(this.contractorDailyRate2) * 5) * 48)) * 4.95
		},
		/**
		 * Calculate the total income for the household
		 * @return {Number}
		 */			
        householdIncome() {
            let annualIncome1 = this.valueToZero(this.soleTraderLatestProfit1) + this.valueToZero(this.partnershipShareholdProfit1) + this.valueToZero(this.directorYearDividends1) + this.valueToZero(this.directorSalaryDrawn1) + this.valueToZero(this.contractorDailyRate1) + this.valueToZero(this.subcontractorAnnualIcome1) + this.valueToZero(this.employedAnnualIncome1)
            let annualIncome2 = this.valueToZero(this.soleTraderLatestProfit2) + this.valueToZero(this.partnershipShareholdProfit2) + this.valueToZero(this.directorYearDividends2) + this.valueToZero(this.directorSalaryDrawn2) + this.valueToZero(this.contractorDailyRate2) + this.valueToZero(this.subcontractorAnnualIcome2) + this.valueToZero(this.employedAnnualIncome2)
            return annualIncome1 + annualIncome2;
        },		
		collateMessageBody() {
			const calculatorData = {
				calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
				calculatorResults:
				`
					Are you purchasing or remortgaging? ${this.startOption}
					Do you have a property in mind? ${this.propertyOption}
					What is the purchase price? £${this.purchasePrice}
					Approximately, what value would you like to purchase at? £${this.purchaseValue}

					What is your required mortgage amount? £${this.mortgageAmount}
					What is the approximate value of your property? £${this.approximateValue}
					What is your current outstanding mortgage amount? £${this.formatEmptyMessageFields( this.outstandingMortgage )}
					Who is your current lender? ${this.currentLender}
					Are you remortgaging with additional borrowing? ${this.additionalBorrowing}
					How much more would you like to borrow? £${this.additionalBorrowAmount}
					
					Number of Applicants? ${this.applicantsCount}

					Applicant1
						Employment status: ${this.formatEmptyMessageFields( this.removeHyphensToUpperCase( this.applicantEmployment1 ) )}

						How long have you been trading? ${this.formatEmptyMessageFields( this.soleTraderYearsTrading1 )}
						Do you have an accountant? ${this.formatEmptyMessageFields( this.soleTraderAccoutantOption1 )}
						Are your tax returns up-to-date? ${this.formatEmptyMessageFields( this.soleTraderTaxReturns1 )}
						Has there been a substantial change in net profit in the last 2 years? ${this.formatEmptyMessageFields( this.soleTraderprofitChange1 )}
						Your latest year's net profit: £${this.formatEmptyMessageFields( this.soleTraderLatestProfit1 )}					

						How long have you been trading? ${this.formatEmptyMessageFields( this.partnershipYearsTrading1 )}
						Do you have an accountant? ${this.formatEmptyMessageFields( this.partnershipAccoutantOption1 )}
						Are your tax returns up-to-date? ${this.formatEmptyMessageFields( this.partnershipTaxReturns1 )}
						Has there been a substantial change in net profit in the last 2 years? ${this.formatEmptyMessageFields( this.partnershipProfitChange1 )}
						What percentage shareholding do you have? ${this.formatEmptyMessageFields( this.partnershipShareholdPercentage1 )}%
						What is your share of the net profit? £${this.formatEmptyMessageFields( this.partnershipShareholdProfit1 )}

						How long have you been a shareholding Director? ${this.formatEmptyMessageFields( this.directorYearsTrading1 )}
						Do you have an accountant? ${this.formatEmptyMessageFields( this.directorAccoutantOption1 )}
						Are your tax returns up-to-date? ${this.formatEmptyMessageFields( this.directorTaxReturns1 )}
						Have you recently changed trading style? ${this.formatEmptyMessageFields( this.directorTradingStyle1 )}
						Your salary drawn: £${this.formatEmptyMessageFields( this.directorSalaryDrawn1 )}
						Dividends for the last 12 months: £${this.formatEmptyMessageFields( this.directorYearDividends1 )}									
						
						Have you been contracting for at least 6 months? ${this.formatEmptyMessageFields( this.contractorMonths1 )}
						If so, how long for? ${this.formatEmptyMessageFields( this.contractorYearsTrading1 )}
						Do you have an accountant? ${this.formatEmptyMessageFields( this.contractorAccountantOption1 )}
						Are you tax returns up to date? ${this.formatEmptyMessageFields( this.contractorTaxReturns1 )}
						What is your daily rate? £${this.formatEmptyMessageFields( this.contractorDailyRate1 )}					
											
						Have you been sub-contracting for at least 12 months? ${this.formatEmptyMessageFields( this.subcontractorMonths1 )}
						If so, how long for? ${this.formatEmptyMessageFields( this.subcontractorYearsTrading1 )}
						Have you worked for the same contractor for 12 months? ${this.formatEmptyMessageFields( this.subcontractorSameContractor1 )}
						Does the company you work for arrange the payment of your tax? ${this.formatEmptyMessageFields( this.subcontractorPaymentOfTax1 )}
						What is your annual income? £${this.formatEmptyMessageFields( this.subcontractorAnnualIcome1 )}
										
						What is your annual income? £${this.formatEmptyMessageFields( this.employedAnnualIncome1 )}
					
					Applicant2
						Employment status: ${this.formatEmptyMessageFields( this.removeHyphensToUpperCase( this.applicantEmployment2 ) )}

						How long have you been trading? ${this.formatEmptyMessageFields( this.soleTraderYearsTrading2 )}
						Do you have an accountant? ${this.formatEmptyMessageFields( this.soleTraderAccoutantOption2 )}
						Are your tax returns up-to-date? ${this.formatEmptyMessageFields( this.soleTraderTaxReturns2 )}
						Has there been a substantial change in net profit in the last 2 years? ${this.formatEmptyMessageFields( this.soleTraderprofitChange2 )}
						Your latest year's net profit: £${this.formatEmptyMessageFields( this.soleTraderLatestProfit2 )}					

						How long have you been trading? ${this.formatEmptyMessageFields( this.partnershipYearsTrading2 )}
						Do you have an accountant? ${this.formatEmptyMessageFields( this.partnershipAccoutantOption2 )}
						Are your tax returns up-to-date? ${this.formatEmptyMessageFields( this.partnershipTaxReturns2 )}
						Has there been a substantial change in net profit in the last 2 years? ${this.formatEmptyMessageFields( this.partnershipProfitChange2 )}
						What percentage shareholding do you have? ${this.formatEmptyMessageFields( this.partnershipShareholdPercentage2 )}%
						What is your share of the net profit? £${this.formatEmptyMessageFields( this.partnershipShareholdProfit2 )}

						How long have you been a shareholding Director? ${this.formatEmptyMessageFields( this.directorYearsTrading2 )}
						Do you have an accountant? ${this.formatEmptyMessageFields( this.directorAccoutantOption2 )}
						Are your tax returns up-to-date? ${this.formatEmptyMessageFields( this.directorTaxReturns2 )}
						Have you recently changed trading style? ${this.formatEmptyMessageFields( this.directorTradingStyle2 )}
						Your salary drawn: £${this.formatEmptyMessageFields( this.directorSalaryDrawn2 )}
						Dividends for the last 12 months: £${this.formatEmptyMessageFields( this.directorYearDividends2 )}									
						
						Have you been contracting for at least 6 months? ${this.formatEmptyMessageFields( this.contractorMonths2 )}
						If so, how long for? ${this.formatEmptyMessageFields( this.contractorYearsTrading2 )}
						Do you have an accountant? ${this.formatEmptyMessageFields( this.contractorAccountantOption2 )}
						Are you tax returns up to date? ${this.formatEmptyMessageFields( this.contractorTaxReturns2 )}
						What is your daily rate? £${this.formatEmptyMessageFields( this.contractorDailyRate2 )}					
											
						Have you been sub-contracting for at least 12 months? ${this.formatEmptyMessageFields( this.subcontractorMonths2 )}
						If so, how long for? ${this.formatEmptyMessageFields( this.subcontractorYearsTrading2 )}
						Have you worked for the same contractor for 12 months? ${this.formatEmptyMessageFields( this.subcontractorSameContractor2 )}
						Does the company you work for arrange the payment of your tax? ${this.formatEmptyMessageFields( this.subcontractorPaymentOfTax2 )}
						What is your annual income? £${this.formatEmptyMessageFields( this.subcontractorAnnualIcome2 )}
										
						What is your annual income? £${this.formatEmptyMessageFields( this.employedAnnualIncome2 )}
				`
			}
			
			return calculatorData;
		}
    }
}
</script>

<style lang="scss">
	@import './styles/calcs.scss';
</style>